import React, { createContext, CSSProperties, PropsWithChildren, useContext, useMemo } from 'react';

import type { DraggableSyntheticListeners, UniqueIdentifier } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
	CONTENT_TYPE_BANNER,
	CONTENT_TYPE_HERO_VIDEO,
	CONTENT_TYPE_PLAYLIST,
	CONTENT_TYPE_SEARCH_FILTER,
} from '../../../../../constants/portalConfigConstant';
import BannerContent from './contentItemType/BannerContent';
import HeroVideoContent from './contentItemType/HeroVideoContent';
import PlaylistContent from './contentItemType/PlaylistContent';
import SearchFilterContent from './contentItemType/SearchFilterContent';

type Context = {
	attributes: Record<string, any>;
	listeners: DraggableSyntheticListeners;
	ref(_node: any): void;
	playlists: any[];
	expandedId: string;
	miniMediaLibraryRef: any;
	accountPreset: string[];
	handleClearSelectedHeroMedia(): void;
	handleUpdateHeroMedia(_key: any, _value: any): void;
	handleUpdateBanner(_key: any, _value: any): void;
	handleUpdatePlaylist(_playlist: any, _key: any, _value: any): void;
	handleUpdateSearchFilter(_id: any, _key: any, _value: any): void;
	handleDeleteContentItem(_id: any): void;
	handleDuplicateContentItem(_id: any): void;
	setExpandedId(_id: string): void;
};

export const SortableItemContext = createContext<Context>({
	attributes: {},
	listeners: undefined,
	ref() {},
	playlists: [],
	expandedId: '',
	miniMediaLibraryRef: null,
	accountPreset: [],
	handleClearSelectedHeroMedia() {},
	handleUpdateHeroMedia() {},
	handleUpdateBanner() {},
	handleUpdatePlaylist() {},
	handleUpdateSearchFilter() {},
	handleDeleteContentItem() {},
	handleDuplicateContentItem() {},
	setExpandedId() {},
});

type ContentItemProps = {
	id: UniqueIdentifier;
	item: any;
	playlists: any[];
	expandedId?: string;
	miniMediaLibraryRef: any;
	accountPreset: string[];
	handleClearSelectedHeroMedia: () => void;
	handleUpdateHeroMedia: (_key: any, _value: any) => void;
	handleUpdateBanner: (_key: any, _value: any) => void;
	handleUpdatePlaylist: (_playlist: any, _key: any, _value: any) => void;
	handleUpdateSearchFilter: (_id: any, _key: any, _value: any) => void;
	handleDeleteContentItem: (_id: any) => void;
	handleDuplicateContentItem: (_id: any) => void;
	setExpandedId?: (_id: string) => void;
};

export const ContentItem = ({
	id,
	item,
	playlists,
	expandedId = '',
	miniMediaLibraryRef,
	accountPreset,
	handleClearSelectedHeroMedia,
	handleUpdateHeroMedia,
	handleUpdateBanner,
	handleUpdatePlaylist,
	handleUpdateSearchFilter,
	handleDeleteContentItem,
	handleDuplicateContentItem,
	setExpandedId = () => {},
}: PropsWithChildren<ContentItemProps>) => {
	const { attributes, isDragging, listeners, setNodeRef, setActivatorNodeRef, transform, transition } = useSortable({
		id,
	});

	const { contentType } = item;

	const context = useMemo(
		() => ({
			attributes,
			listeners,
			ref: setActivatorNodeRef,
			playlists,
			expandedId,
			accountPreset,
			miniMediaLibraryRef,
			handleClearSelectedHeroMedia,
			handleUpdateHeroMedia,
			handleUpdateBanner,
			handleUpdatePlaylist,
			handleUpdateSearchFilter,
			handleDeleteContentItem,
			handleDuplicateContentItem,
			setExpandedId,
		}),
		[
			attributes,
			listeners,
			setActivatorNodeRef,
			playlists,
			expandedId,
			miniMediaLibraryRef,
			accountPreset,
			handleClearSelectedHeroMedia,
			handleUpdateHeroMedia,
			handleUpdateBanner,
			handleUpdatePlaylist,
			handleUpdateSearchFilter,
			handleDeleteContentItem,
			handleDuplicateContentItem,
			setExpandedId,
		]
	);

	const style: CSSProperties = {
		opacity: isDragging ? 0.4 : undefined,
		transform: CSS.Translate.toString(transform),
		transition,
	};

	return (
		<SortableItemContext.Provider value={context}>
			<div ref={setNodeRef} style={{ ...style }}>
				{contentType === CONTENT_TYPE_BANNER && <BannerContent item={item} />}
				{contentType === CONTENT_TYPE_HERO_VIDEO && <HeroVideoContent item={item} />}
				{contentType === CONTENT_TYPE_PLAYLIST && <PlaylistContent item={item} />}
				{contentType === CONTENT_TYPE_SEARCH_FILTER && <SearchFilterContent item={item} />}
			</div>
		</SortableItemContext.Provider>
	);
};

export const DragHandle = () => {
	const { attributes, listeners, ref } = useContext(SortableItemContext);

	return (
		<button className="DragHandle" {...attributes} {...listeners} ref={ref}>
			<svg viewBox="0 0 20 20" width="12">
				<path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"></path>
			</svg>
		</button>
	);
};
